import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import type { FeatureCollection, Point } from "geojson";
import { BookA, CircleHelp, Compass, FolderOpen, Map as MapIcon, MapPin, Network, Search, Shield } from "lucide-react";
import { Fragment, type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";

import { api } from "../api/client";
import { MenuLabel } from "../components/Menu";
import { motion, usePresence } from "../components/presence";
import { Kbd } from "../components/ui";
import { useDismiss } from "../components/useDismiss";
import { VIEW_META, ViewIcon } from "../components/viewMeta";
import { glossaryEntries } from "../help/glossary";
import { useHelp } from "../help/helpStore";
import { helpTopics, matches, screenTitle } from "../help/topics";
import { useReferenceEnemy } from "../enemyDisplay";
import { useTrialExport } from "../licence/trial";
import { unitDesignation } from "../i18n/designation";
import { k, t } from "../i18n/i18n";
import { formatMgrs } from "../map/MapCanvas";
import { useDeployment } from "../map/units/useDeployment";
import { type ObjectsFilter, useUi, type ViewKey } from "../state/ui";
import { showMissionField } from "../views/openMissionData";
import { useOrbatsTarget } from "../views/orbats/orbatsTarget";
import { unitsOnMap } from "../views/OrbatPanel";
import { forceAndMission, searchText } from "./home/dossier";
import {
  type CommandAction,
  type CommandGroup,
  type CommandResult,
  type CommandSources,
  glossaryAnchor,
  searchCommands,
} from "./commandSearch";
import { declinable } from "../views/declination/declination";

/** Titres des groupes de résultats. */
const GROUP_LABELS: Record<CommandGroup, string> = {
  views: k("Vues"),
  units: k("Unités"),
  dossiers: k("Dossiers"),
  places: k("Lieux"),
  help: k("Aide"),
  glossary: k("Glossaire"),
};

/** Vues proposées, dans l'ordre de la barre latérale (préparation, rédaction, conduite, documents). */
const VIEW_ORDER: { key: ViewKey; objects?: ObjectsFilter }[] = [
  { key: "mission" },
  { key: "manoeuvre" },
  { key: "terrain" },
  { key: "coa" },
  { key: "logistics" },
  { key: "maintenance" },
  { key: "coherence" },
  { key: "country" },
  { key: "briefing" },
  { key: "oplan" },
  { key: "order" },
  { key: "timeline" },
  { key: "evaluation" },
  { key: "retex" },
  { key: "received" },
  { key: "declination" },
  { key: "exports" },
  { key: "orbats" },
  { key: "enemies" },
];

function ResultIcon({ result }: { result: CommandResult }): ReactNode {
  const action = result.action;
  switch (action.type) {
    case "view":
      return <ViewIcon view={action.view} size={14} />;
    case "unit":
      return <Shield size={14} aria-hidden />;
    case "dossier":
      return <FolderOpen size={14} aria-hidden />;
    case "camp":
      return <MapIcon size={14} aria-hidden />;
    case "locality":
      return <MapPin size={14} aria-hidden />;
    case "onboarding":
      return <Compass size={14} aria-hidden />;
    case "new-odb":
      return <Network size={14} aria-hidden />;
    case "glossary":
      return <BookA size={14} aria-hidden />;
    case "help":
      return action.tab === "glossary" ? <BookA size={14} aria-hidden /> : <CircleHelp size={14} aria-hidden />;
    default:
      return <CircleHelp size={14} aria-hidden />;
  }
}

/** Recherche ⌘K (R9 : fenêtre au plan 50, voile `bg-ink/20`, Échap par la pile partagée) : vues, unités du
 *  dispositif au temps affiché, dossiers, camps et localités du camp courant, fiches d'aide et glossaire. Résultats
 *  groupés, parcourus aux flèches (la ligne active reste visible), Entrée pour ouvrir. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const mapSlug = useUi((s) => s.mapSlug);
  const exerciseId = useUi((s) => s.exerciseId);
  const phase = useUi((s) => s.phase);
  const setMapSlug = useUi((s) => s.setMapSlug);
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const setVisible = useUi((s) => s.setVisible);
  const setView = useUi((s) => s.setView);
  const showObjects = useUi((s) => s.showObjects);
  const select = useUi((s) => s.select);
  const setExercise = useUi((s) => s.setExercise);
  const openHelp = useHelp((s) => s.openHelp);
  const openOnboarding = useHelp((s) => s.openOnboarding);
  const { mounted, closing } = usePresence(open);
  // Échap ferme la recherche seule (pile partagée) : une unité sélectionnée sur la carte le reste.
  useDismiss(open && !closing, onClose);

  const { data: maps = [] } = useQuery({ queryKey: ["maps"], queryFn: api.maps, enabled: open });
  const { data: grid } = useQuery({
    queryKey: ["layer", mapSlug, "grid"],
    queryFn: () => api.layer(mapSlug!, "grid") as Promise<FeatureCollection<Point>>,
    enabled: open && Boolean(mapSlug),
  });
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises, enabled: open });
  const { data: deployment } = useDeployment();
  const referenceEnemy = useReferenceEnemy();
  const trial = useTrialExport().blocked;

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
    }
  }, [open]);

  const results = useMemo<CommandResult[]>(() => {
    // Calculés seulement à l'écran ; gardés pendant la sortie (pas de liste vide qui s'efface).
    if (!mounted) return [];
    const sources: CommandSources = {
      // RETEX absent face à l'ennemi générique (force non française, registre B39).
      // Ordres subordonnés : seulement depuis un dossier de brigade ou de division (docs/20 §13).
      views: VIEW_ORDER.filter(
        (v) =>
          (exerciseId || v.key !== "objects") &&
          (referenceEnemy || v.key !== "retex") &&
          // Licence d'essai (registre F44) : « Exports » est grisé dans la barre latérale, la recherche n'y mène pas.
          (!trial || v.key !== "exports") &&
          (v.key !== "declination" || declinable(exercises.find((e) => e.id === exerciseId)?.parameters.level)) &&
          // OPLAN : dossier de corps d'armée seulement (docs/08 §25).
          (v.key !== "oplan" || exercises.find((e) => e.id === exerciseId)?.parameters.level === "CORPS"),
      ).map((v) => ({
        key: v.key,
        objects: v.objects,
        label: t(VIEW_META[v.key].label),
      })),
      units: unitsOnMap(deployment?.units, phase).map((u) => {
        const position = u.positions[phase]!;
        return {
          code: u.code,
          label: unitDesignation(u.display_name || u.designation),
          detail: [
            u.side === "ENI" ? t("Ennemi") : u.side === "AMI" ? t("Ami") : null,
            u.display_name ? unitDesignation(u.designation) : null,
            unitDesignation(u.code),
          ]
            .filter(Boolean)
            .join(" · "),
          lngLat: [position.lon, position.lat] as [number, number],
        };
      }),
      dossiers: exercises.map((ex) => ({ id: ex.id, name: ex.name, detail: forceAndMission(ex), mapSlug: ex.map_slug, text: searchText(ex) })),
      camps: maps.map((m) => ({ slug: m.slug, name: m.name, detail: t("Camp") })),
      localities: (grid?.features ?? []).flatMap((f) => {
        const name = String(f.properties?.locality ?? "");
        if (!name) return [];
        const [lng, lat] = f.geometry.coordinates as [number, number];
        return [{ key: String(f.properties?.square), name, detail: formatMgrs(lng, lat, 3), lngLat: [lng, lat] as [number, number] }];
      }),
      topics: helpTopics().map((topic) => ({
        id: topic.id,
        screen: topic.screen,
        title: topic.title,
        detail: screenTitle(topic.screen) ?? "",
        matches: (q: string) => matches(topic, q),
      })),
      viewActions: [
        {
          id: "new-odb",
          label: t("Nouvel ordre de bataille"),
          keywords: "ordre de bataille ODB créer nouveau APP-6 OTAN order of battle ORBAT new create NATO",
          action: { type: "new-odb" },
        },
      ],
      helpActions: [
        { id: "all", label: t("Toute l'aide"), keywords: `${t("Aide et glossaire")} aide help`, action: { type: "help", tab: "all" } },
        { id: "glossary", label: t("Glossaire des sigles"), keywords: "glossaire sigles abréviations glossary abbreviations acronyms", action: { type: "help", tab: "glossary" } },
        { id: "tour", label: t("Prise en main"), keywords: "visite guidée prise en main onboarding tour", action: { type: "onboarding" } },
      ],
      glossary: glossaryEntries(),
    };
    return searchCommands(query, sources);
  }, [mounted, query, exerciseId, deployment, phase, exercises, maps, grid, referenceEnemy, trial]);

  // Ligne active toujours visible quand on la déplace aux flèches.
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active]);

  if (!mounted) return null;

  const run = (action: CommandAction) => {
    switch (action.type) {
      case "view":
        if (action.objects) showObjects(action.objects);
        else setView(action.view);
        break;
      case "unit":
        select({ layer: "unit", code: action.code, lngLat: action.lngLat });
        requestFlyTo(action.lngLat, 13);
        break;
      case "dossier":
        if (action.id !== exerciseId) setExercise(action.id, action.mapSlug);
        setView("mission");
        break;
      case "camp": {
        // Dossier ouvert, brouillon d'un nouveau dossier ou Données initiales affichées : le camp est celui du dossier,
        // il se change dans les Données initiales (jamais le décor d'un camp sous les unités d'un autre). Sinon, la
        // carte montre simplement le camp choisi.
        const ui = useUi.getState();
        if (action.slug !== mapSlug && (exerciseId || ui.deploymentParams || ui.view === "mission"))
          showMissionField("dossier-camp", t("Le camp du dossier se choisit dans les Données initiales."));
        else setMapSlug(action.slug);
        break;
      }
      case "locality":
        setVisible("grid", true);
        requestFlyTo(action.lngLat, 13);
        break;
      case "topic":
        openHelp({ screen: action.screen, topic: action.id });
        break;
      case "help":
        openHelp({ tab: action.tab });
        break;
      case "onboarding":
        openOnboarding();
        break;
      case "new-odb":
        useOrbatsTarget.getState().open("AMI", null, { create: { side: "AMI", kind: "gtia" } });
        setView("orbats");
        break;
      case "glossary":
        openHelp({ tab: "glossary", topic: glossaryAnchor(action.term) });
        break;
    }
  };
  const choose = (result: CommandResult | undefined) => {
    if (!result) return;
    onClose();
    run(result.action);
  };
  const optionId = (i: number) => `${listId}-${i}`;

  return (
    <div
      className={clsx("fixed inset-0 top-(--gx-strip-h) z-50 flex items-start justify-center bg-ink/20 px-4 pt-[12vh]", motion("fade", closing))}
      onMouseDown={closing ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("Rechercher")}
        className={clsx("w-full max-w-[560px] overflow-hidden rounded-xl border border-line bg-surface shadow-float", motion("zoom", closing))}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex h-11 items-center gap-2.5 border-b border-line px-4">
          <Search size={16} className="shrink-0 text-ink-muted" aria-hidden />
          <input
            autoFocus
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={results.length ? optionId(active) : undefined}
            aria-label={t("Rechercher une vue, une unité, un lieu…")}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                e.preventDefault();
                choose(results[active]);
              }
            }}
            placeholder={t("Rechercher une vue, une unité, un lieu…")}
            className="h-full min-w-0 flex-1 bg-transparent text-title text-ink outline-none placeholder:text-ink-muted"
          />
          <Kbd hideOnTouch>{t("Échap")}</Kbd>
        </div>
        <div ref={listRef} id={listId} role="listbox" aria-label={t("Résultats")} className="max-h-[50vh] overflow-y-auto p-1">
          {results.length === 0 && <p className="px-3 py-6 text-center text-ui text-ink-muted">{t("Aucun résultat.")}</p>}
          {results.map((result, i) => (
            <Fragment key={result.id}>
              {(i === 0 || results[i - 1]!.group !== result.group) && <MenuLabel>{t(GROUP_LABELS[result.group])}</MenuLabel>}
              <button
                type="button"
                role="option"
                id={optionId(i)}
                data-index={i}
                aria-selected={i === active}
                tabIndex={-1}
                onMouseMove={() => setActive(i)}
                onClick={() => choose(result)}
                className={clsx(
                  "flex min-h-8 w-full scroll-my-8 items-center gap-2 rounded-md px-2 py-1 text-left text-ui transition-colors",
                  i === active ? "bg-selected" : "hover:bg-hover",
                )}
              >
                <span className="flex w-4 shrink-0 justify-center text-ink-muted">
                  <ResultIcon result={result} />
                </span>
                <span className="min-w-0 flex-1 truncate text-ink">{result.label}</span>
                {result.detail && <span className="min-w-0 max-w-[55%] shrink truncate text-meta text-ink-muted">{result.detail}</span>}
              </button>
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
